import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Icon, Modal, useToast } from '@w7/ui'
import {
  flagOn,
  padlockOn,
  postGameLock,
  postGameStatus,
  type GameLockKind,
  type GameStatusGame,
} from '@/api/map/gameLock'
import type { Account } from '@/data/types'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canWriteGameLock } from '@/app/staffAccess'
import './downline.css'

type ProductRow = {
  label: string
  kind?: GameLockKind
  game?: GameStatusGame
  blockedBySportsbook?: boolean
}

const PRODUCTS: ProductRow[] = [
  { label: 'Sportsbook', kind: 'sportsbook', game: 'sportsbook' },
  { label: 'Live Casino', kind: 'casino', game: 'casino' },
  { label: 'Card Game', kind: 'card', game: 'cardGame', blockedBySportsbook: true },
  { label: 'Live Game', kind: 'liveGame', game: 'liveGame', blockedBySportsbook: true },
  { label: 'Premium-Sportsbook', kind: 'premiumSportsbook', game: 'premiumSportsbook', blockedBySportsbook: true },
  { label: 'Horse Racing', kind: 'horseRacing', game: 'horseRacing', blockedBySportsbook: true },
  { label: 'Google Authenticator', game: 'googleAuthenticator' },
  { label: 'Transfer Lock', kind: 'transfer' },
]

const CRICKET: ProductRow[] = [
  { label: 'Cricket', game: 'cricket', blockedBySportsbook: true },
  { label: 'Line Session', kind: 'lineSession', game: 'lineSession', blockedBySportsbook: true },
  { label: 'Tied Match', kind: 'tiedMatch', game: 'tiedMatch', blockedBySportsbook: true },
  { label: 'UOF Markets', game: 'uofMarkets', blockedBySportsbook: true },
  { label: 'Match odds', kind: 'matchOdds', game: 'matchOdds', blockedBySportsbook: true },
  { label: 'Bookmaker', kind: 'bookmaker', game: 'bookmaker', blockedBySportsbook: true },
  { label: 'Completed Match', kind: 'completedMatch', game: 'completedMatch', blockedBySportsbook: true },
  { label: 'Custom Session', kind: 'customSession', game: 'customSession', blockedBySportsbook: true },
]

const SPORTS: ProductRow[] = [
  { label: 'Esports', game: 'esports', blockedBySportsbook: true },
  { label: 'Greyhound Racing', game: 'greyhoundRacing', blockedBySportsbook: true },
  { label: 'Darts', game: 'darts', blockedBySportsbook: true },
  { label: 'Election', game: 'election', blockedBySportsbook: true },
  { label: 'Tennis', game: 'tennis', blockedBySportsbook: true },
  { label: 'Soccer', game: 'soccer', blockedBySportsbook: true },
  { label: 'Soccer Super', game: 'soccerSuper', blockedBySportsbook: true },
  { label: 'Horse Racing', game: 'sportHorseRacing', blockedBySportsbook: true },
  { label: 'Volleyball', game: 'volleyball', blockedBySportsbook: true },
  { label: 'Snooker', game: 'snooker', blockedBySportsbook: true },
  { label: 'Rugby League', game: 'rugbyLeague', blockedBySportsbook: true },
  { label: 'Artemis', game: 'artemis', blockedBySportsbook: true },
  { label: 'Rugby Union', game: 'rugbyUnion', blockedBySportsbook: true },
]

function LockToggle({
  locked,
  disabled,
  label,
  onClick,
}: {
  locked: boolean
  disabled?: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`w7-events-lock${locked ? ' is-locked' : ' is-unlocked'}${disabled ? ' is-muted' : ''}`}
      disabled={disabled}
      aria-label={
        disabled
          ? `${label} lock unavailable`
          : locked
            ? `Unlock ${label}`
            : `Lock ${label}`
      }
      onClick={onClick}
    >
      <Icon name={locked ? 'lock' : 'unlock'} size={16} />
    </button>
  )
}

function GameRow({
  row,
  account,
  sportsbookLocked,
  closed,
  busy,
  readOnly,
  onLock,
  onStatus,
}: {
  row: ProductRow
  account: Account
  sportsbookLocked: boolean
  closed: boolean
  busy: boolean
  readOnly: boolean
  onLock: (kind: GameLockKind) => void
  onStatus: (game: GameStatusGame, enabled: boolean) => void
}) {
  const locked = row.kind ? padlockOn(account.gameLocks, row.kind) : false
  const enabled = row.game ? flagOn(account.gameFlags, row.game) : true
  const padlockDisabled =
    readOnly || closed || busy || (row.blockedBySportsbook && sportsbookLocked && !locked)
  return (
    <div className="w7-game-lock-row">
      <span className="w7-game-lock-label">{row.label}</span>
      {row.kind ? (
        <LockToggle
          locked={locked}
          disabled={padlockDisabled}
          label={row.label}
          onClick={() => onLock(row.kind!)}
        />
      ) : (
        <span className="w7-game-lock-pad-spacer" />
      )}
      {row.game ? (
        <label className="w7-game-lock-switch">
          <input
            type="checkbox"
            checked={enabled}
            disabled={readOnly || closed || busy}
            aria-label={`${row.label} ${enabled ? 'on' : 'off'}`}
            onChange={(event) => onStatus(row.game!, event.target.checked)}
          />
          <span>{enabled ? 'On' : 'Off'}</span>
        </label>
      ) : null}
    </div>
  )
}

/**
 * Lab lock-modal — product padlocks, On/Off, Sportswise, Authenticator, Transfer.
 * Global lock is not part of this surface.
 */
export function GameLockModal({
  account,
  onClose,
}: {
  account: Account
  onClose: () => void
}) {
  const toast = useToast()
  const me = useStaffMe()
  const writeAllowed = canWriteGameLock(me.data)
  const qc = useQueryClient()
  const closed = account.status === 'closed'
  const sportsbookLocked = padlockOn(account.gameLocks, 'sportsbook')
  const [confirmKind, setConfirmKind] = useState<GameLockKind | null>(null)

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['downline'] })
    void qc.invalidateQueries({ queryKey: ['account'] })
    void qc.invalidateQueries({ queryKey: ['children'] })
  }

  const lockMut = useMutation({
    mutationFn: (kind: GameLockKind) => postGameLock(account.id, kind),
    onSuccess: (res) => {
      toast.success(res.message)
      setConfirmKind(null)
      invalidate()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Game lock failed')
    },
  })

  const statusMut = useMutation({
    mutationFn: (opts: { game: GameStatusGame; enabled: boolean }) =>
      postGameStatus(account.id, opts.game, opts.enabled),
    onSuccess: (res) => {
      toast.success(res.message)
      invalidate()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Game status failed')
    },
  })

  const busy = lockMut.isPending || statusMut.isPending
  const rowProps = {
    account,
    sportsbookLocked,
    closed,
    busy,
    readOnly: !writeAllowed,
    onLock: (kind: GameLockKind) => {
      if (!writeAllowed) return
      setConfirmKind(kind)
    },
    onStatus: (game: GameStatusGame, enabled: boolean) => {
      if (!writeAllowed) {
        toast.error('Missing access grant: Member Lock/Unlock')
        return
      }
      statusMut.mutate({ game, enabled })
    },
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Lock"
      subtitle={writeAllowed ? account.username : `${account.username} · view only`}
      size="lg"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="w7-game-lock-grid w7-game-lock-products">
        {PRODUCTS.map((row) => (
          <GameRow key={`${row.label}-${row.kind ?? row.game}`} row={row} {...rowProps} />
        ))}
      </div>
      <h3 className="w7-game-lock-section">Sportswise</h3>
      {sportsbookLocked ? <p className="w7-dimmer">Sportsbook locked</p> : null}
      <div className="w7-game-lock-sports">
        <div className="w7-game-lock-cricket">
          {CRICKET.map((row) => (
            <GameRow key={row.label} row={row} {...rowProps} />
          ))}
        </div>
        <div className="w7-game-lock-grid">
          {SPORTS.map((row) => (
            <GameRow key={row.game} row={row} {...rowProps} />
          ))}
        </div>
      </div>
      {confirmKind && writeAllowed ? (
        <div className="w7-game-lock-confirm" role="dialog" aria-label="Confirm game lock">
          <p>
            Lock or unlock {confirmKind} for {account.username}?
          </p>
          <div className="w7-row" style={{ gap: 8 }}>
            <Button type="button" variant="secondary" onClick={() => setConfirmKind(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={busy}
              onClick={() => lockMut.mutate(confirmKind)}
            >
              Confirm
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
